import { defineStore } from 'pinia';
import type { ICatalogCategory, ICatalogFilter, ICatalogResult, ICatalog, ICatalogBreadcrumbs, ICatalogBrand, ICatalogFiltersSelected, TCatalogSorting, ISelectedFilterItem, ISelectedFilter, ISetSelectedFilter, ISetSelectedBrand } from '~/shared/interfaces/catalog';

interface ICatalogState {
  catalogCategories: ICatalogCategory[]
  currentSlug: string[]
  catalog: ICatalog | null

  filters: ICatalogFilter[]
  catalogBreadcrumbs: ICatalogBreadcrumbs[]
  inStock: boolean

  uuidSlug: string
  openedCategory: ICatalogCategory | null
  currentCategories: ICatalogCategory[] | null
  prevCategories: ICatalogCategory[] | null
  needProductsReplace: boolean
  currentEventFilter: boolean
  selectedFilters: ICatalogFiltersSelected

  showSortingModal: boolean
  showFiltersModal: boolean

  sortingList: Record<TCatalogSorting, string>
}

const useCatalogStore = defineStore('catalog', {
  state: (): ICatalogState => ({
    catalogCategories: [],
    currentSlug: [],
    catalog: null,

    filters: [],
    inStock: false,
    catalogBreadcrumbs: [],

    uuidSlug: '',
    currentCategories: null,
    openedCategory: null,
    prevCategories: null,
    needProductsReplace: false,
    currentEventFilter: true,
    selectedFilters: {
      page: 1,
      sorting: 'popular',
      filters: [],
      brands: [],
    },

    showSortingModal: false,
    showFiltersModal: false,

    sortingList: {
      popular: 'По умолчанию',
      firstLowPrice: 'Дешевле',
      firstHighPrice: 'Дороже',
    }
  }),
  actions: {
    setCatalogInfo({ filters, catalog, breadcrumbs }: ICatalogResult) {
      if (!this.needProductsReplace || !this.catalog) {
        this.catalog = catalog;
      }else if(this.catalog) {
        this.catalog = {
          ...catalog,
          products: this.currentEventFilter ? [...catalog.products] : [...this.catalog.products, ...catalog.products],
        };

        this.needProductsReplace = false;
      }

      this.filters = filters;
      this.catalogBreadcrumbs = breadcrumbs;
    },
    setCatalogCategories(categories: ICatalogCategory[]) {
      this.catalogCategories = categories;
    },
    setSelectedFilter(newFilter: ISelectedFilter) {
      const { filterId, values } = newFilter;
      const selectedFilterIndex = this.selectedFilters.filters.findIndex(filterItem => filterItem.filterId == filterId);

      if (selectedFilterIndex == -1) {
        if (!values.length) {
          return;
        } else {
          this.selectedFilters.filters.push(newFilter);
        }
      } else {
        if (!values.length) {
          this.selectedFilters.filters.splice(selectedFilterIndex, 1);
        } else {
          this.selectedFilters.filters[selectedFilterIndex].values = values;
        }
      }
    },
    replaceSelectedBrandList(newBand: ISelectedFilter) {
      this.selectedFilters.brands = newBand.values.map(({ name, value }) => {
        return {
          name,
          value
        }
      })
    },
    replaceInitialSelectedList(selectedList: ICatalogFiltersSelected) {
      this.selectedFilters = selectedList;
    },
    setProductsReplaceStatus(state: boolean) {
      this.needProductsReplace = state;
    },
    setBrandsSelectedStatus({ name, value, active }: ISetSelectedBrand) {
      this.currentEventFilter = true
      if (active) {
        this.selectedFilters.brands.push({
          name,
          value
        })
      } else {
        this.selectedFilters.brands = this.selectedFilters.brands.filter(brand => brand.value != value);
      }

      this.selectedFilters.page = 1;
      this.setActiveStatusForFilter({ value, active, filterId: 'brands' });
    },
    setCurrentPage(page: number) {
      this.currentEventFilter = false
      this.selectedFilters.page = page;
    },
    setFiltersSelectedStatus({ name, value, active, filterId, filterName }: ISetSelectedFilter) {
      this.currentEventFilter = true
      const selectedFilterIndex = this.selectedFilters.filters.findIndex(filter => filter.filterId == filterId);

      if (!active) {
        this.selectedFilters.filters[selectedFilterIndex].values = this.selectedFilters.filters[selectedFilterIndex].values.filter(filterValue => filterValue.value != value);
        if (!this.selectedFilters.filters[selectedFilterIndex].values.length) {
          this.selectedFilters.filters.splice(selectedFilterIndex, 1);
        }
      } else {
        if (selectedFilterIndex != -1) {
          this.selectedFilters.filters[selectedFilterIndex].values.push({
            name,
            value,
          })
        } else {
          this.selectedFilters.filters.push({
            filterId,
            filterName,
            values: [{
              name,
              value,
            }]
          })
        }
      }
      this.selectedFilters.page = 1;
      this.setActiveStatusForFilter({ value, active, filterId });
    },
    setActiveStatusForFilter({ value, active, filterId }: Pick<ISetSelectedFilter, 'active' | 'filterId' | 'value'>) {
      const filterValue = this.filters.find(filter => filter.id == filterId)?.values.find(filterValue => filterValue.value == value);

      if (!filterValue) {
        return;
      }

      filterValue.active = active;
    },
    setCatalogSorting(sort: TCatalogSorting) {
      this.selectedFilters.sorting = sort;
      this.selectedFilters.page = 1;
    },
    setSlug(slug: string[]) {
      this.currentSlug = slug;

      let catalogCategoriesList: ICatalogCategory[] = [...this.catalogCategories];

      for (let i = 0; i < slug.length; i++) {
        const category: ICatalogCategory | undefined = catalogCategoriesList.find(category => category.slug == slug[i]);

        if (category) {
          const { childCategories, uuid } = category;

          this.currentCategories = childCategories.length ? childCategories : null;
          this.uuidSlug = uuid;

          this.openedCategory = category;
          this.prevCategories = [...catalogCategoriesList].filter(categoryItem => categoryItem.uuid != this.uuidSlug);

          catalogCategoriesList = childCategories;
        } else {
          this.currentCategories = null;
        }
      }
    },
    clearSelectedFiltersList() {
      this.selectedFilters.brands = [];
      this.selectedFilters.filters = [];
    },
    resetSelectedList() {
      this.selectedFilters = {
        page: 0,
        sorting: 'popular',
        filters: [],
        brands: [],
      };
    },
    setFiltersModalStatus(status: boolean) {
      this.showFiltersModal = status;
    },
    setSortingModalStatus(status: boolean) {
      this.showSortingModal = status;
    },
  },
});

export default useCatalogStore;
